import { inject, Injectable } from '@angular/core';
import { WINDOW } from '@campus/browser';
import { ReplaySubject } from 'rxjs';

declare global {
  interface Window {
    MathJax: {
      tex?: {
        inlineMath?: string[][];
        displayMath?: string[][];
      };
      options: {
        ignoreHtmlClass: string;
        processHtmlClass: string;
      };
      typesetPromise?: (code?) => void;
      startup?: {
        promise: Promise<any>;
        document: {
          clear: () => void;
          updateDocument: () => void;
        };
      };
      tex2chtml?: (code: string, options?: any) => any;
    };
  }
}

export interface MathJaxConfig {
  source: string;
  integrety: string;
  id: string;
}

// KEEP IN SYNC WITH polpo-api/common/lib/mathjax-renderer.js normalize().
// Strips inline tags Froala wraps around parts of an expression (e.g. style spans)
// and decodes the HTML entities Froala emits — including the numeric form &#39;
// used for the apostrophe in f'(x). Also folds the literal U+00A0 that DomSanitizer
// emits when it decodes &nbsp; in the upstream LatexPreviewService pipeline.
//
// Order: tags → named (apos/quot) → numeric (&#NN; / &#xHH;) → circumflex → other named → amp last.
// `&amp;` runs last so patterns like `&amp;lt;` end up as the literal `&lt;` instead
// of being double-decoded to `<`.
function normalizeLatex(s: string): string {
  return s
    .replace(/<[^>]+>/g, '')
    .replace(/&apos;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&#x([0-9a-fA-F]+);/g, (_, h) => String.fromCharCode(parseInt(h, 16)))
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
    .replace(/&circ;/g, '^')
    .replace(/ˆ/g, '^')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ')
    .replace(/ /g, ' ')
    .replace(/&amp;/g, '&');
}

@Injectable({
  providedIn: 'root',
})
export class MathJaxService {
  private initializePromise: Promise<void>;
  private ready$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  private window = inject(WINDOW);

  private mathJax: MathJaxConfig = {
    source: 'https://cdnjs.cloudflare.com/ajax/libs/mathjax/3.2.2/es5/tex-chtml.min.js',
    integrety: 'sha512-T8xxpazDtODy3WOP/c6hvQI2O9UPdARlDWE0CvH1Cfqc0TXZF6GZcEKL7tIR8VbfS/7s/J6C+VOqrD6hIo++vQ==',
    id: 'MathJaxScript',
  };

  constructor() {
    this.registerMathJaxAsync(this.mathJax).then(() => {
      this.ready$.next(true);
    });
  }

  protected async registerMathJaxAsync(mathJax: MathJaxConfig): Promise<void> {
    if (document.getElementById(mathJax.id) || this.initializePromise) {
      return this.initializePromise;
    }
    this.window.MathJax = {
      tex: {
        inlineMath: [['$', '$'], ['\\(', '\\)']],
        displayMath: [['$$', '$$'], ['\\[', '\\]']],
      },
      options: {
        ignoreHtmlClass: 'no-mathjax',
        processHtmlClass: 'process-mathjax',
      },
    };

    this.initializePromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = mathJax.source;
      script.integrity = mathJax.integrety;
      script.id = mathJax.id;
      script.crossOrigin = 'anonymous';
      script.async = false;
      script.onload = () => {
        resolve();
      };
      script.onerror = () => {
        reject();
      };
      document.getElementsByTagName('head')[0].appendChild(script);
    });

    return this.initializePromise;
  }

  public ready() {
    return this.ready$;
  }

  public async render(input: string) {
    if (input === null || input === undefined) return '';

    if (!this.window.MathJax) {
      await this.registerMathJaxAsync(this.mathJax);
    }

    await this.window.MathJax.startup.promise;
    // Supports Froala-style \[...\] (display) and \(...\) (inline), and legacy $...$ / $$...$$ delimiters.
    // $$...$$ must precede $...$ via alternation order so the inline group never consumes one $ of a $$ pair.
    // Lookarounds on the single-$ branch ensure it does not match a $ that is part of $$.
    // The capture groups intentionally allow < and > so Froala-wrapped math (e.g. coloured spans) is matched;
    // normalizeLatex() then strips inline tags before MathJax sees the expression. This mirrors the BE renderer.
    const regex = /\\\[([\s\S]+?)\\\]|\\\(([\s\S]+?)\\\)|\$\$([\s\S]+?)\$\$|(?<!\$)\$(?!\$)([\s\S]+?)(?<!\$)\$(?!\$)/g;
    const output = input.replace(regex, (_match, displayBackslash, inlineBackslash, displayDollar, inlineDollar) => {
      const isDisplay = displayBackslash !== undefined || displayDollar !== undefined;
      const rawLatex = displayBackslash ?? inlineBackslash ?? displayDollar ?? inlineDollar;
      const latex = normalizeLatex(rawLatex);
      const html = this.window.MathJax.tex2chtml(latex, { display: isDisplay });

      return html.outerHTML;
    });

    this.window.MathJax.startup.document.clear();
    this.window.MathJax.startup.document.updateDocument();

    return output;
  }
}
